import { BrowserRouter, Routes, Route, useLocation, Outlet, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster, toast } from "sonner";
import "./App.css";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider, useTenant } from "./contexts/TenantContext";
import { initCapacitor } from "./lib/capacitor";
import { alertFromPush } from "./lib/orderAlert";
import OrderAlertHost from "./components/OrderAlertHost";

import BottomNav from "./components/BottomNav";
import DesktopNav from "./components/DesktopNav";
import StickyCart from "./components/StickyCart";
import ZoneOfflineBanner from "./components/ZoneOfflineBanner";
import OwnerConsoleRedirect from "./components/OwnerConsoleRedirect";
import BatteryOptimizationNudge from "./components/BatteryOptimizationNudge";
import { AppBootLoader } from "./components/Loaders";
import { hasCompletedPermissions } from "./pages/PermissionsPage";
import {
    ROLE_LOCK, SHOW_RIDER, SHOW_MERCHANT, SHOW_ADMIN, SHOW_SUPER, SHOW_ZONEADMIN,
    ONBOARDING_APP,
} from "./lib/buildLock";

import HomePage from "./pages/customer/HomePage";
import MainPage from "./pages/customer/MainPage";
import CategoryFeedPage from "./pages/customer/CategoryFeedPage";
import BrowseProductsPage from "./pages/customer/BrowseProductsPage";
import GroupCategoryFeedPage from "./pages/customer/GroupCategoryFeedPage";
import AllCategoriesPage from "./pages/customer/AllCategoriesPage";
import LoginPage from "./pages/customer/LoginPage";
import RestaurantDetailPage from "./pages/customer/RestaurantDetailPage";
import ProductDetailsPage from "./pages/customer/ProductDetailsPage";
import CartPage from "./pages/customer/CartPage";
import CheckoutPage from "./pages/customer/CheckoutPage";
import OrderTrackPage from "./pages/customer/OrderTrackPage";
import OrderHistoryPage from "./pages/customer/OrderHistoryPage";
import OrderGroupPage from "./pages/customer/OrderGroupPage";
import ProfilePage from "./pages/customer/ProfilePage";
import ProfileEditPage from "./pages/customer/ProfileEditPage";
import WalletPage from "./pages/customer/WalletPage";
import CouponsPage from "./pages/customer/CouponsPage";
import StoreCouponsPage from "./pages/customer/StoreCouponsPage";
import SearchPage from "./pages/customer/SearchPage";
import AddressesPage from "./pages/customer/AddressesPage";
import RequireAuth from "./components/RequireAuth";
import LegacyOrderRedirect from "./components/LegacyOrderRedirect";
import { LseTicketPage } from "./components/lse/LseSupport";
import LseBasketsPage from "./components/lse/LseBaskets";
import LseLayout, { LseOverview } from "./pages/admin/lse/LseLayout";
import LseZones from "./pages/admin/lse/LseZones";
import LseWarehouses from "./pages/admin/lse/LseWarehouses";
import LsePicking from "./pages/admin/lse/LsePicking";
import LseRoutes from "./pages/admin/lse/LseRoutes";
import LseDispatch from "./pages/admin/lse/LseDispatch";
import LseRefunds from "./pages/admin/lse/LseRefunds";
import LseSupportAdmin from "./pages/admin/lse/LseSupportAdmin";
import LseOffers from "./pages/admin/lse/LseOffers";
import LseMessages from "./pages/admin/lse/LseMessages";
import LseAnalytics from "./pages/admin/lse/LseAnalytics";
import LseAudit from "./pages/admin/lse/LseAudit";
import LseSettings from "./pages/admin/lse/LseSettings";
import CustomerBookings from "./pages/customer/CustomerBookings";
import CustomerCoins from "./pages/customer/CustomerCoins";
import CustomerReferral from "./pages/customer/CustomerReferral";
import SupportPage from "./pages/customer/SupportPage";
import NotificationsPage from "./pages/customer/NotificationsPage";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRestaurants from "./pages/admin/AdminRestaurants";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminInventory from "./pages/admin/AdminInventory";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminCharges from "./pages/admin/AdminCharges";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminZones from "./pages/admin/AdminZones";
import AdminRiders from "./pages/admin/AdminRiders";
import AdminBranding from "./pages/admin/AdminBranding";
import AdminBulkNotifications from "./pages/admin/AdminBulkNotifications";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminOwners from "./pages/admin/AdminOwners";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminBusinessCategories from "./pages/admin/AdminBusinessCategories";
import AdminCategoryGroups from "./pages/admin/AdminCategoryGroups";
import AdminHeroTiles from "./pages/admin/AdminHeroTiles";
import AdminHomeSections from "./pages/admin/AdminHomeSections";
import AdminTheme from "./pages/admin/AdminTheme";
import AdminCoinsConfig from "./pages/admin/AdminCoinsConfig";
import AdminReferralConfig from "./pages/admin/AdminReferralConfig";
import AdminShopSubmissions from "./pages/admin/AdminShopSubmissions";
import AdminBilling from "./pages/admin/AdminBilling";
import ImpersonationBanner from "./components/ImpersonationBanner";
import AdminReports from "./pages/admin/AdminReports";
import AdminHours from "./pages/admin/AdminHours";
import AdminRiderLedger from "./pages/admin/AdminRiderLedger";
import AdminSettlements from "./pages/admin/AdminSettlements";
import AdminShiprocket from "./pages/admin/AdminShiprocket";
import AdminCommissionInvoices from "./pages/admin/AdminCommissionInvoices";
import AdminPaymentRecords from "./pages/admin/AdminPaymentRecords";
import MerchantCommissionInvoices from "./pages/merchant/MerchantCommissionInvoices";
import AdminBookingSettlements from "./pages/admin/AdminBookingSettlements";
import AdminPolicies from "./pages/admin/AdminPolicies";
import AdminZoneAdmins from "./pages/admin/AdminZoneAdmins";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminImports from "./pages/admin/AdminImports";
import AdminWorkflow from "./pages/admin/AdminWorkflow";
import AdminSupportContact from "./pages/admin/AdminSupportContact";
import AdminWalletWithdrawals from "./pages/admin/AdminWalletWithdrawals";
import AdminReturns from "./pages/admin/AdminReturns";
import DailySettlementReport from "./pages/admin/reports/DailySettlementReport";

import MerchantLayout from "./pages/merchant/MerchantLayout";
import MerchantDashboard from "./pages/merchant/MerchantDashboard";
import MerchantOrders from "./pages/merchant/MerchantOrders";
import MerchantOrderDetail from "./pages/merchant/MerchantOrderDetail";
import MerchantMenu from "./pages/merchant/MerchantMenu";
import MerchantCategories from "./pages/merchant/MerchantCategories";
import MerchantInventory from "./pages/merchant/MerchantInventory";
import MerchantOffers from "./pages/merchant/MerchantOffers";
import MerchantCoupons from "./pages/merchant/MerchantCoupons";
import MerchantSettlements from "./pages/merchant/MerchantSettlements";
import MerchantAnalytics from "./pages/merchant/MerchantAnalytics";
import MerchantHours from "./pages/merchant/MerchantHours";
import MerchantPrinter from "./pages/merchant/MerchantPrinter";
import MerchantBookings from "./pages/merchant/MerchantBookings";
import MerchantImports from "./pages/merchant/MerchantImports";
import MerchantShops from "./pages/merchant/MerchantShops";
import MerchantAuditLog from "./pages/merchant/MerchantAuditLog";

import RiderEarnings from "./pages/rider/RiderEarnings";

import ZoneAdminDashboard from "./pages/zoneadmin/ZoneAdminDashboard";

import SuperLayout from "./pages/super/SuperLayout";
import SuperDashboard from "./pages/super/SuperDashboard";
import SuperTenants from "./pages/super/SuperTenants";
import SuperFeatures from "./pages/super/SuperFeatures";
import SuperBilling from "./pages/super/SuperBilling";
import SuperPlans from "./pages/super/SuperPlans";
import SuperSettings from "./pages/super/SuperSettings";
import SuperTickets from "./pages/super/SuperTickets";
import SuperFirebase from "./pages/super/SuperFirebase";
import SuperPolicies from "./pages/super/SuperPolicies";
import SuperMaintenance from "./pages/super/SuperMaintenance";
import SuperBackups from "./pages/super/SuperBackups";

import RiderLayout from "./pages/rider/RiderLayout";
import RiderHome from "./pages/rider/RiderHome";
import RiderOrderDetail from "./pages/rider/RiderOrderDetail";
import RiderRoute from "./pages/rider/RiderRoute";
import RiderHistory from "./pages/rider/RiderHistory";
import RiderAccount from "./pages/rider/RiderAccount";

import LegalPage from "./pages/LegalPage";
import PermissionsPage from "./pages/PermissionsPage";
import NotFoundPage from "./pages/NotFoundPage";

/**
 * SwapManifest — swaps the PWA manifest based on the current path so the
 * Rider PWA installs as a separate icon ("Bizil Rider") with its own
 * standalone scope, theme color, and start_url.
 */
function SwapManifest() {
    const { pathname } = useLocation();
    useEffect(() => {
        const el = document.getElementById("dynamic-manifest");
        if (!el) return;
        const want = pathname.startsWith("/rider") ? "/rider-manifest.json" : "/manifest.json";
        if (el.getAttribute("href") !== want) el.setAttribute("href", want);
        // Match theme color too
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute("content", pathname.startsWith("/rider") ? "#5B21B6" : "#22C55E");
    }, [pathname]);
    return null;
}

function TenantUnavailable({ status, slug }) {
    const title = status === 423 ? "Tenant Suspended"
        : status === 410 ? "Tenant No Longer Available"
        : "Tenant Not Found";
    const help = status === 423
        ? "This tenant has been temporarily suspended. Please contact support if you think this is a mistake."
        : status === 410
        ? "This tenant has been removed. Please reach out to the team you ordered from for further help."
        : "We couldn't find a tenant matching this link. Please double-check the URL or visit the home page.";
    const clearAndGoHome = () => {
        try {
            window.localStorage.removeItem("bizil_tenant_slug");
            window.sessionStorage.removeItem("bizil_tenant_slug");
        } catch { /* ignore */ }
        window.location.assign("/");
    };
    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-background" data-testid="tenant-unavailable">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold mb-2" data-testid="tenant-unavailable-title">{title}</h1>
            <p className="text-sm text-muted-foreground max-w-sm mb-1" data-testid="tenant-unavailable-message">{help}</p>
            <p className="text-xs text-muted-foreground/70 font-mono mb-6">slug: <span className="text-foreground">{slug}</span></p>
            <button
                onClick={clearAndGoHome}
                data-testid="tenant-unavailable-home"
                className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 font-bold text-sm"
            >Go to home</button>
        </div>
    );
}

function CustomerShell() {
    const { loading, error } = useTenant();
    const { user } = useAuth();
    const { pathname, search } = useLocation();
    if (loading) return <AppBootLoader />;
    if (error) return <TenantUnavailable {...error} />;
    // Role-locked builds (iter50, iter53). The Rider APK never renders
    // the customer shell — it ships only the rider routes. The Customer
    // APK is a *union* (Customer + Merchant) so both `/` and `/merchant`
    // resolve normally; no redirect needed there.
    if (ROLE_LOCK === "RIDER") return <Navigate to={pathname.startsWith("/rider") ? pathname : "/rider"} replace />;
    const showShell = !pathname.startsWith("/admin") && !pathname.startsWith("/super") && !pathname.startsWith("/merchant") && !pathname.startsWith("/rider");
    if (!showShell) return <Outlet />;
    // First-launch permissions onboarding (iter47, deferred in iter56) —
    // runs once per device per app role, BUT only after the user has
    // logged in. Anonymous browsing skips the onboarding so the login
    // CTA stays the first thing a new visitor sees. Also skipped on the
    // onboarding screen itself, the auth flow and legal pages so it
    // can't trap the user in a redirect loop.
    const skipPermsRoutes = pathname.startsWith("/onboarding/")
        || pathname.startsWith("/legal/")
        || pathname.startsWith("/login");
    if (user && !skipPermsRoutes && !hasCompletedPermissions()) {
        const nextUrl = pathname + (search || "");
        return <Navigate to={`/onboarding/permissions?app=${ONBOARDING_APP}&next=${encodeURIComponent(nextUrl)}`} replace />;
    }
    const showNav = !["/login", "/checkout", "/cart"].includes(pathname) && !pathname.startsWith("/orders/") && !pathname.startsWith("/order-groups/") && !pathname.startsWith("/product/") && !pathname.startsWith("/onboarding/") && !pathname.startsWith("/legal/")
        && !pathname.startsWith("/support/tickets/");   // ticket conversation has its own reply bar (Location Service Engine)
    // Desktop (≥1024px) top bar — on every customer screen except the focused login / onboarding / legal ones. Phones
    // keep their back-button screens (cart, checkout, product, order) exactly as before; DesktopNav is lg-only CSS.
    const showDesktopNav = !pathname.startsWith("/login") && !pathname.startsWith("/onboarding/") && !pathname.startsWith("/legal/");
    return (
        <div
            className="pwa-bg safe-pl safe-pr"
            // iter146 — was `min-h-screen` (100vh). `body` already
            // reserves a full 100dvh INCLUDING the safe-area padding
            // (border-box); re-claiming another independent 100vh here
            // stacks on top of that and overflows the real viewport by
            // safe-top+safe-bottom on notched iOS devices — see the
            // matching `.pwa-shell` comment in index.css for the full
            // writeup (same fix, same reasoning, one level up).
            style={{ minHeight: "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px))" }}
        >
            {showDesktopNav && <DesktopNav />}
            <div className={`pwa-shell ${showNav ? "pb-24 lg:pb-0" : ""}`}>
                <ZoneOfflineBanner />
                <OwnerConsoleRedirect />
                <Outlet />
                <StickyCart />
                <BatteryOptimizationNudge />
                {showNav && <BottomNav />}
            </div>
        </div>
    );
}

export default function App() {
    useEffect(() => {
        // Init the Capacitor bridge (status bar + push notifications) when
        // we're running inside a native WebView. Safe no-op in browser.
        initCapacitor();
        // Surface foreground push notifications as a toast so the user
        // doesn't miss them while the app is open. New-order alerts
        // (merchant: order just came in; rider: order available) get a
        // longer-lived, visually distinct toast — capacitor.js already
        // tags ``isNewOrder`` on the event based on the push's topic —
        // since a merchant/rider genuinely can't afford to miss one,
        // matching the louder sound/vibration treatment those pushes
        // already get at the OS/foreground-chime level.
        const onPush = (e) => {
            const n = e.detail || {};
            // OrderAlertHost shows these as a dialog (or drops a duplicate); a toast would repeat it.
            if (n.persistentAlert && alertFromPush(n)) return;
            const title = n.title || "New notification";
            const body = n.body || "";
            if (n.isNewOrder) {
                toast.warning(title, { description: body, duration: 10000 });
            } else {
                toast(title, { description: body });
            }
        };
        window.addEventListener("bizil:push", onPush);
        return () => window.removeEventListener("bizil:push", onPush);
    }, []);
    return (
        <BrowserRouter>
            <TenantProvider>
                <AuthProvider>
                    {/* iter91/iter146 — Sonner's default ``top-center`` position
                        renders the toast at viewport top ``y=0``, which on iOS
                        Capacitor builds overlaps the status bar / Dynamic Island
                        (user-reported screenshots Feb 2026 + Aug 2026). Push it
                        below the safe-area inset so the toast lives in the same
                        band as native iOS notifications.
                        Landmine (iter146): Sonner splits desktop vs. mobile
                        positioning into TWO separate CSS vars — ``--offset-top``
                        (``offset`` prop, used ≥600px viewport) and
                        ``--mobile-offset-top`` (``mobileOffset`` prop, used
                        <600px — i.e. EVERY phone). Setting only ``offset`` (as
                        iter91 did) fixed tablet/desktop but left phones on
                        Sonner's hardcoded ``16px`` mobile default, which ignores
                        the safe area — hence the still-overlapping status bar on
                        actual devices. Both props must carry the same
                        safe-area-aware value. */}
                    <Toaster
                        richColors
                        position="top-center"
                        offset="calc(env(safe-area-inset-top, 0px) + 16px)"
                        mobileOffset="calc(env(safe-area-inset-top, 0px) + 16px)"
                        toastOptions={{
                            // Cap width on small viewports so the toast doesn't
                            // run edge-to-edge under the notch.
                            className: "max-w-[calc(100vw-1.5rem)]",
                        }}
                    />
                    <OrderAlertHost />
                    <SwapManifest />
                    <ImpersonationBanner />
                    <Routes>
                        <Route element={<CustomerShell />}>
                            <Route path="/" element={<MainPage />} />
                            <Route path="/food" element={<HomePage />} />
                            <Route path="/categories" element={<AllCategoriesPage />} />
                            <Route path="/c/:slug" element={<CategoryFeedPage />} />
                            <Route path="/c/:slug/products" element={<BrowseProductsPage />} />
                            <Route path="/products" element={<BrowseProductsPage />} />
                            <Route path="/g/:slug" element={<GroupCategoryFeedPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/restaurant/:id" element={<RestaurantDetailPage />} />
                            <Route path="/product/:id" element={<ProductDetailsPage />} />
                            {/* RequireAuth waits for the session before rendering, so a signed-in customer who opens or
                                refreshes /cart is no longer sent to /login while the session is still loading (the cart
                                page decided "signed out" before AuthContext had answered). Signed-out visitors still go to
                                /login and come back to the cart (with its query string) afterwards. */}
                            <Route path="/cart" element={<RequireAuth><CartPage /></RequireAuth>} />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/orders" element={<RequireAuth><OrderHistoryPage /></RequireAuth>} />
                            <Route path="/orders/:id" element={<RequireAuth><OrderTrackPage /></RequireAuth>} />
                            {/* Multi-shop checkout: the shop orders one checkout produced (reached only from such an order). */}
                            <Route path="/order-groups/:id" element={<RequireAuth><OrderGroupPage /></RequireAuth>} />
                            {/* Legacy / mistyped singular link (the notifications list used /order/<id>) → the order page. */}
                            <Route path="/order/:id" element={<LegacyOrderRedirect />} />
                            <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                            <Route path="/profile/edit" element={<RequireAuth><ProfileEditPage /></RequireAuth>} />
                            <Route path="/wallet" element={<RequireAuth><WalletPage /></RequireAuth>} />
                            <Route path="/coupons" element={<RequireAuth><CouponsPage /></RequireAuth>} />
                            <Route path="/store-coupons" element={<StoreCouponsPage />} />
                            <Route path="/addresses" element={<RequireAuth><AddressesPage /></RequireAuth>} />
                            <Route path="/bookings" element={<RequireAuth><CustomerBookings /></RequireAuth>} />
                            <Route path="/coins" element={<RequireAuth><CustomerCoins /></RequireAuth>} />
                            <Route path="/refer" element={<RequireAuth><CustomerReferral /></RequireAuth>} />
                            <Route path="/support" element={<SupportPage />} />
                            <Route path="/support/tickets/:id" element={<RequireAuth><LseTicketPage /></RequireAuth>} />
                            <Route path="/baskets" element={<RequireAuth><LseBasketsPage /></RequireAuth>} />
                            <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
                            <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
                            <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
                            <Route path="/onboarding/permissions" element={<PermissionsPage />} />

                            {SHOW_ADMIN && (
                            <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="restaurants" element={<AdminRestaurants />} />
                                <Route path="categories" element={<AdminCategories />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="inventory" element={<AdminInventory />} />
                                <Route path="inventory" element={<AdminInventory />} />
                                <Route path="pricing" element={<AdminPricing />} />
                                <Route path="payments" element={<AdminPayments />} />
                                <Route path="charges" element={<AdminCharges />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="orders/:id" element={<AdminOrderDetail />} />
                                <Route path="coupons" element={<AdminCoupons />} />
                                <Route path="zones" element={<AdminZones />} />
                                <Route path="riders" element={<AdminRiders />} />
                                <Route path="owners" element={<AdminOwners />} />
                                <Route path="team" element={<AdminTeam />} />
                                <Route path="customers" element={<AdminCustomers />} />
                                <Route path="branding" element={<AdminBranding />} />
                                <Route path="bulk-notifications" element={<AdminBulkNotifications />} />
                                <Route path="notifications" element={<AdminNotifications />} />
                                <Route path="banners" element={<AdminBanners />} />
                                <Route path="business-categories" element={<AdminBusinessCategories />} />
                                <Route path="category-groups" element={<AdminCategoryGroups />} />
                                <Route path="hero-tiles" element={<AdminHeroTiles />} />
                                <Route path="home-sections" element={<AdminHomeSections />} />
                                <Route path="theme" element={<AdminTheme />} />
                                <Route path="coins" element={<AdminCoinsConfig />} />
                                <Route path="referral" element={<AdminReferralConfig />} />
                                <Route path="shop-submissions" element={<AdminShopSubmissions />} />
                                <Route path="billing" element={<AdminBilling />} />
                                <Route path="hours" element={<AdminHours />} />
                                <Route path="rider-ledger" element={<AdminRiderLedger />} />
                                <Route path="settlements" element={<AdminSettlements />} />
                                <Route path="shiprocket" element={<AdminShiprocket />} />
                                <Route path="commission-invoices" element={<AdminCommissionInvoices />} />
                                <Route path="payment-records" element={<AdminPaymentRecords />} />
                                <Route path="booking-settlements" element={<AdminBookingSettlements />} />
                                <Route path="zone-admins" element={<AdminZoneAdmins />} />
                                <Route path="bookings" element={<AdminBookings />} />
                                <Route path="imports" element={<AdminImports />} />
                                <Route path="workflow" element={<AdminWorkflow />} />
                                <Route path="support-contact" element={<AdminSupportContact />} />
                                <Route path="withdrawals" element={<AdminWalletWithdrawals />} />
                                <Route path="returns" element={<AdminReturns />} />
                                <Route path="policies" element={<AdminPolicies />} />
                                <Route path="reports" element={<AdminReports />}>
                                    <Route path="daily-settlement" element={<DailySettlementReport />} />
                                </Route>
                                {/* Location Service Engine (tenant-gated: the hub and every API refuse when it's off). */}
                                <Route path="lse" element={<LseLayout />}>
                                    <Route index element={<LseOverview />} />
                                    <Route path="zones" element={<LseZones />} />
                                    <Route path="warehouses" element={<LseWarehouses />} />
                                    <Route path="picking" element={<LsePicking />} />
                                    <Route path="routes" element={<LseRoutes />} />
                                    <Route path="dispatch" element={<LseDispatch />} />
                                    <Route path="refunds" element={<LseRefunds />} />
                                    <Route path="support" element={<LseSupportAdmin />} />
                                    <Route path="offers" element={<LseOffers />} />
                                    <Route path="messages" element={<LseMessages />} />
                                    <Route path="analytics" element={<LseAnalytics />} />
                                    <Route path="audit" element={<LseAudit />} />
                                    <Route path="settings" element={<LseSettings />} />
                                </Route>
                            </Route>
                            )}

                            {SHOW_MERCHANT && (
                            <Route path="/merchant" element={<MerchantLayout />}>
                                <Route index element={<MerchantDashboard />} />
                                <Route path="shops" element={<MerchantShops />} />
                                <Route path="orders" element={<MerchantOrders />} />
                                <Route path="orders/:id" element={<MerchantOrderDetail />} />
                                <Route path="categories" element={<MerchantCategories />} />
                                <Route path="menu" element={<MerchantMenu />} />
                                <Route path="inventory" element={<MerchantInventory />} />
                                <Route path="offers" element={<MerchantOffers />} />
                                <Route path="coupons" element={<MerchantCoupons />} />
                                <Route path="settlements" element={<MerchantSettlements />} />
                                <Route path="commission-invoices" element={<MerchantCommissionInvoices />} />
                                <Route path="analytics" element={<MerchantAnalytics />} />
                                <Route path="hours" element={<MerchantHours />} />
                                <Route path="bookings" element={<MerchantBookings />} />
                                <Route path="imports" element={<MerchantImports />} />
                                <Route path="audit-log" element={<MerchantAuditLog />} />
                                <Route path="printer" element={<MerchantPrinter />} />
                                <Route path="picking" element={<LsePicking />} />
                            </Route>
                            )}

                            {SHOW_SUPER && (
                            <Route path="/super" element={<SuperLayout />}>
                                <Route index element={<SuperDashboard />} />
                                <Route path="tenants" element={<SuperTenants />} />
                                <Route path="features" element={<SuperFeatures />} />
                                <Route path="plans" element={<SuperPlans />} />
                                <Route path="billing" element={<SuperBilling />} />
                                <Route path="firebase" element={<SuperFirebase />} />
                                <Route path="settings" element={<SuperSettings />} />
                                <Route path="tickets" element={<SuperTickets />} />
                                <Route path="maintenance" element={<SuperMaintenance />} />
                                <Route path="backups" element={<SuperBackups />} />
                                <Route path="policies" element={<SuperPolicies />} />
                            </Route>
                            )}

                            {SHOW_RIDER && (
                            <Route path="/rider" element={<RiderLayout />}>
                                <Route index element={<RiderHome />} />
                                <Route path="earnings" element={<RiderEarnings />} />
                                <Route path="history" element={<RiderHistory />} />
                                <Route path="account" element={<RiderAccount />} />
                                <Route path="orders/:id" element={<RiderOrderDetail />} />
                                <Route path="route" element={<RiderRoute />} />
                            </Route>
                            )}

                            {/* Catch-all — must stay last. Without it, any unmatched
                                path (mistyped URL, stale bookmark) rendered a blank
                                white screen since the <Outlet/> had nothing to show. */}
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>

                        {/* Zone admin lives outside the customer shell — its own full-screen layout (no customer bottom-nav). */}
                        {SHOW_ZONEADMIN && (
                            <Route path="/zone-admin" element={<RequireAuth><ZoneAdminDashboard /></RequireAuth>} />
                        )}
                    </Routes>
                </AuthProvider>
            </TenantProvider>
        </BrowserRouter>
    );
}
